import { Component } from '@theme/component';

const COLLECT_ADDITIONAL_LINES_EVENT = 'product-form:collect-additional-lines';

/**
 * @typedef {object} AdditionalLine
 * @property {string} variantId
 * @property {number} quantity
 *
 * @typedef {object} CollectAdditionalLinesDetail
 * @property {Element} source
 * @property {number} quantity
 * @property {AdditionalLine[]} lines
 */

class ProductAddOns extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const { signal } = this.#abortController;
    this.dataset.enhanced = 'true';
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.#scope?.addEventListener(COLLECT_ADDITIONAL_LINES_EVENT, this.#onCollectAdditionalLines, { signal });
    this.#synchronizeCards();
    this.#updateProductForm(false);
  }

  disconnectedCallback() {
    const scope = this.#scope;
    this.#abortController.abort();
    super.disconnectedCallback();

    queueMicrotask(() => {
      if (scope?.isConnected) ProductAddOns.updateProductForms(scope, false);
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.dataset.enhanced = 'true';
    this.#synchronizeCards();
    this.#updateProductForm(false);
  }

  get #scope() {
    return this.closest('.product-details, featured-product-information, .shopify-section');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('a, button, input, label, select, textarea, [data-add-on-variants]')) return;

    const card = target.closest('[data-product-add-on-card]');
    const checkbox = card?.querySelector('[data-product-add-on]');
    if (!(card instanceof HTMLElement) || !(checkbox instanceof HTMLInputElement) || checkbox.disabled) return;

    checkbox.checked = !checkbox.checked;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-add-on-variant-choice]')) {
      const card = target.closest('[data-product-add-on-card]');
      const checkbox = card?.querySelector('[data-product-add-on]');
      if (!(card instanceof HTMLElement) || !(checkbox instanceof HTMLInputElement)) return;

      this.#copyVariantData(target, checkbox);
      checkbox.checked = !checkbox.disabled;
      this.#applyCardPresentation(card, target);
      card.classList.toggle('is-selected', checkbox.checked);
    } else if (target.matches('[data-product-add-on]')) {
      const card = target.closest('[data-product-add-on-card]');
      if (card instanceof HTMLElement) card.classList.toggle('is-selected', target.checked);
    } else {
      return;
    }

    this.#updateProductForm(true);
  };

  /** @param {Event} event */
  #onCollectAdditionalLines = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const detail = /** @type {CollectAdditionalLinesDetail | undefined} */ (event.detail);
    if (!detail || !Array.isArray(detail.lines) || !(detail.source instanceof Element)) return;
    if (detail.source.closest('.product-details, featured-product-information, .shopify-section') !== this.#scope) return;

    const quantity = Number(detail.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) return;

    const selectedAddOns = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('[data-product-add-on]:checked:not(:disabled)')
    );

    for (const addOn of selectedAddOns) {
      const variantId = addOn.dataset.addOnVariantId;
      if (variantId && /^\d+$/.test(variantId)) detail.lines.push({ variantId, quantity });
    }
  };

  #synchronizeCards() {
    for (const card of this.querySelectorAll('[data-product-add-on-card]')) {
      if (!(card instanceof HTMLElement)) continue;

      const checkbox = card.querySelector('[data-product-add-on]');
      const choice = card.querySelector('[data-add-on-variant-choice]:checked');
      if (!(checkbox instanceof HTMLInputElement)) continue;

      if (choice instanceof HTMLInputElement) {
        this.#copyVariantData(choice, checkbox);
        this.#applyCardPresentation(card, choice);
      }

      card.classList.toggle('is-selected', checkbox.checked);
    }
  }

  /**
   * @param {HTMLInputElement} source
   * @param {HTMLInputElement} destination
   */
  #copyVariantData(source, destination) {
    const keys = [
      'variantId',
      'variantPrice',
      'variantFree',
      'variantFreeMessage',
      'variantPromotionalMessage',
      'variantImage',
      'variantImageSrcset',
      'variantImageAlt',
    ];

    for (const key of keys) {
      const value = source.dataset[key];
      if (value === undefined) delete destination.dataset[key];
      else destination.dataset[key] = value;
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLInputElement} variant
   */
  #applyCardPresentation(card, variant) {
    const isFree = variant.dataset.variantFree === 'true';
    const price = card.querySelector('[data-add-on-price]');
    const freeMessage = card.querySelector('[data-add-on-free-message]');
    const promotionalMessage = card.querySelector('[data-add-on-promotional-message]');
    const image = card.querySelector('[data-add-on-image]');

    card.classList.toggle('is-free', isFree);

    if (price instanceof HTMLElement) {
      price.textContent = variant.dataset.variantPrice || '';
      price.classList.toggle('is-original-price', isFree);
    }

    if (freeMessage instanceof HTMLElement) {
      freeMessage.textContent = isFree ? variant.dataset.variantFreeMessage || '' : '';
      freeMessage.hidden = !isFree || !freeMessage.textContent;
    }

    if (promotionalMessage instanceof HTMLElement) {
      promotionalMessage.textContent = variant.dataset.variantPromotionalMessage || '';
      promotionalMessage.hidden = !promotionalMessage.textContent;
    }

    if (image instanceof HTMLImageElement) {
      const imageUrl = variant.dataset.variantImage;
      image.hidden = !imageUrl;
      card.classList.toggle('has-media', Boolean(imageUrl));

      if (imageUrl) {
        image.src = imageUrl;
        image.alt = variant.dataset.variantImageAlt || '';
        const srcset = variant.dataset.variantImageSrcset;
        if (srcset) image.srcset = srcset;
        else image.removeAttribute('srcset');
      }
    }
  }

  /** @param {boolean} announce */
  #updateProductForm(announce) {
    const scope = this.#scope;
    if (scope) ProductAddOns.updateProductForms(scope, announce);
  }

  /**
   * @param {Element} scope
   * @param {boolean} announce
   */
  static updateProductForms(scope, announce) {
    const hasAdditionalLines = Boolean(
      scope.querySelector('product-add-ons [data-product-add-on]:checked:not(:disabled)')
    );

    for (const productForm of scope.querySelectorAll('product-form-component')) {
      const previouslyHadAdditionalLines = productForm.dataset.hasAdditionalLines === 'true';
      productForm.dataset.hasAdditionalLines = hasAdditionalLines ? 'true' : 'false';

      if (announce && hasAdditionalLines && !previouslyHadAdditionalLines) {
        const addOns = scope.querySelector('product-add-ons');
        const status = addOns?.querySelector('[data-add-ons-status]');
        if (status instanceof HTMLElement) {
          status.textContent = addOns?.getAttribute('data-accelerated-checkout-message') || '';
        }
      } else if (!hasAdditionalLines) {
        const status = scope.querySelector('product-add-ons [data-add-ons-status]');
        if (status instanceof HTMLElement) status.textContent = '';
      }
    }
  }
}

if (!customElements.get('product-add-ons')) {
  customElements.define('product-add-ons', ProductAddOns);
}
